import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { tasksAPI, achievementsAPI, usersAPI, fileAPI } from '../services/api';
import { Task, UserTask, Achievement } from '../types';
import { getErrorMessage } from '../utils/errorUtils';
import PageHeader from '../components/PageHeader';
import DataTable, { Column } from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import SidePanel from '../components/SidePanel';
import FileViewer from '../components/FileViewer';
import { extractTaskFileNames } from '../utils/fileUtils';
import { useFileViewer } from '../hooks/useFileViewer';
import TaskFileList from '../components/TaskFileList';
import '../styles/global.css';

const AdminTasks: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pendingTasks, setPendingTasks] = useState<UserTask[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [users, setUsers] = useState<any[]>([]); // Добавим пользователей
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [editTaskIndex, setEditTaskIndex] = useState(-1);
  const [showTaskPanel, setShowTaskPanel] = useState(false);
  const [selectedTask, setSelectedTask] = useState<UserTask | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    achievement_id: 0,
    percentage_value: 10
  });

  const fileViewer = useFileViewer();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tasksData, pendingData, achievementsData, usersData] = await Promise.all([
        tasksAPI.getTasks(),
        tasksAPI.getPendingTasks(),
        achievementsAPI.getAchievements(),
        usersAPI.getUsers()
      ]);
      setTasks(tasksData);
      setPendingTasks(pendingData);
      setAchievements(achievementsData);
      setUsers(usersData);
    } catch (err: any) {
      setError(getErrorMessage(err) || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (userId: number) => {
    const user = users.find(u => u.id === userId);
    return user ? (user.full_name || user.username) : 'Неизвестный пользователь';
  };

  const getTaskInfo = (userTask: UserTask) => {
    const task = tasks.find(t => t.id === userTask.task_id);
    return task || { title: 'Задание #' + userTask.task_id, description: 'Нет описания', achievement_id: 0, percentage_value: 0 };
  };

  const getAchievementTitle = (achievementId: number) => {
    const achievement = achievements.find(a => a.id === achievementId);
    return achievement ? achievement.title : 'Неизвестное достижение';
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.achievement_id === 0) {
      setError('Выберите достижение');
      return;
    }

    try {
      await achievementsAPI.createTask(formData.achievement_id, {
        title: formData.title,
        description: formData.description,
        percentage_value: formData.percentage_value
      });
      closeTaskPanel();
      loadData();
    } catch (err: any) {
      setError(getErrorMessage(err) || 'Ошибка создания задания');
    }
  };

  const handleUpdateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTask) return;

    try {
      await tasksAPI.updateTask(editingTask.id, {
        title: formData.title,
        description: formData.description,
        achievement_id: formData.achievement_id,
        percentage_value: formData.percentage_value
      });
      closeTaskPanel();
      loadData();
    } catch (err: any) {
      setError(getErrorMessage(err) || 'Ошибка обновления задания');
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить это задание?')) return;

    try {
      await tasksAPI.deleteTask(taskId);
      loadData();
    } catch (err: any) {
      setError(getErrorMessage(err) || 'Ошибка удаления задания');
    }
  };

  const openCreatePanel = () => {
    setEditingTask(null);
    setEditTaskIndex(-1);
    setFormData({ title: '', description: '', achievement_id: 0, percentage_value: 10 });
    setShowTaskPanel(true);
  };

  const startEditTask = (task: Task) => {
    const idx = tasks.findIndex(t => t.id === task.id);
    setEditingTask(task);
    setEditTaskIndex(idx);
    setFormData({
      title: task.title,
      description: task.description,
      achievement_id: task.achievement_id,
      percentage_value: task.percentage_value,
    });
    setShowTaskPanel(true);
  };

  const closeTaskPanel = () => {
    setShowTaskPanel(false);
    setEditingTask(null);
    setEditTaskIndex(-1);
    setFormData({ title: '', description: '', achievement_id: 0, percentage_value: 10 });
  };

  const handleTaskFormSubmit = (e: React.FormEvent) => {
    if (editingTask) return handleUpdateTask(e);
    return handleCreateTask(e);
  };

  const handleReviewTask = async (userTaskId: number, approved: boolean, reviewComment?: string) => {
    try {
      await tasksAPI.reviewTask(userTaskId, approved, reviewComment);
      loadData();
    } catch (err: any) {
      setError(getErrorMessage(err) || 'Ошибка проверки задания');
    }
  };

  const closeTaskModal = () => {
    setShowTaskModal(false);
    setSelectedTask(null);
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div className="container">
        <div className="error">
          У вас нет прав для доступа к этой странице
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  const taskColumns: Column<Task>[] = [
    {
      key: 'title',
      header: 'Задание',
      render: (task) => (
        <div>
          <strong style={{ color: 'var(--primary-orange)' }}>{task.title}</strong>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>#{task.id}</div>
        </div>
      ),
    },
    {
      key: 'achievement',
      header: 'Достижение',
      render: (task) => (
        <span className="status-badge status-pending" style={{ fontWeight: 500, textTransform: 'none' }}>
          {getAchievementTitle(task.achievement_id)}
        </span>
      ),
    },
    {
      key: 'percentage_value',
      header: 'Вес',
      width: '70px',
      render: (task) => (
        <span className="status-badge status-submitted">+{task.percentage_value}%</span>
      ),
    },
  ];

  const renderExpandedTask = (task: Task) => (
    <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ flex: 1 }}>
        <div className="exp-label">Описание</div>
        <div className="exp-value">{task.description}</div>
      </div>
      <div className="exp-actions" onClick={(e) => e.stopPropagation()}>
        <button className="btn btn-primary btn-sm" onClick={() => startEditTask(task)}>Редактировать</button>
        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteTask(task.id)}>Удалить</button>
      </div>
    </div>
  );

  return (
    <div className="container" style={{ paddingTop: '1.5rem', paddingBottom: '2rem' }}>
      <PageHeader
        title="Управление заданиями"
        actions={<button className="btn btn-primary" onClick={openCreatePanel}>+ Создать задание</button>}
      />

      {error && <div className="error">{error}</div>}

      <SidePanel
        isOpen={showTaskPanel}
        onClose={closeTaskPanel}
        title={editingTask ? `Редактировать задание #${editingTask.id}` : 'Создать задание'}
        onPrev={editingTask ? () => startEditTask(tasks[editTaskIndex - 1]) : undefined}
        onNext={editingTask ? () => startEditTask(tasks[editTaskIndex + 1]) : undefined}
        hasPrev={editTaskIndex > 0}
        hasNext={editTaskIndex < tasks.length - 1}
        navLabel={editingTask ? `${editTaskIndex + 1} из ${tasks.length}` : undefined}
      >
        <form onSubmit={handleTaskFormSubmit}>
          <div className="form-group">
            <label className="form-label">Название задания</label>
            <input
              type="text"
              className="form-control"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="Введите название"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Достижение</label>
            <select
              className="form-control"
              value={formData.achievement_id}
              onChange={(e) => setFormData({...formData, achievement_id: parseInt(e.target.value)})}
              required
            >
              <option value={0}>Выберите достижение</option>
              {achievements.map(a => (
                <option key={a.id} value={a.id}>{a.title}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Описание</label>
            <textarea
              className="form-control"
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Описание задания"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">% выполнения</label>
            <input
              type="number"
              className="form-control"
              value={formData.percentage_value}
              onChange={(e) => setFormData({...formData, percentage_value: parseFloat(e.target.value)})}
              min="1" max="100"
              required
            />
          </div>
          <div className="side-panel__footer">
            <button type="button" className="btn btn-secondary" onClick={closeTaskPanel}>Отмена</button>
            <button type="submit" className="btn btn-primary">{editingTask ? 'Сохранить' : 'Создать'}</button>
          </div>
        </form>
      </SidePanel>

      <DataTable
        columns={taskColumns}
        data={tasks}
        rowKey={(t) => t.id}
        emptyText="Нет созданных заданий. Создайте первое задание с помощью кнопки выше."
        minWidth="400px"
        renderExpanded={renderExpandedTask}
      />

      {/* Просмотрщик файлов */}
      {fileViewer.isOpen && (
        <FileViewer
          files={fileViewer.files}
          currentIndex={fileViewer.currentIndex}
          folder="tasks"
          onClose={fileViewer.close}
          onNext={fileViewer.next}
          onPrev={fileViewer.prev}
          onDownload={(filename) => fileAPI.downloadFile(filename)}
        />
      )}

      {/* Просмотр задания на проверку */}
      <Modal isOpen={!!selectedTask && showTaskModal} onClose={closeTaskModal} maxWidth="900px">
        {selectedTask && (() => {
          const taskInfo = getTaskInfo(selectedTask);
          const fileNames = extractTaskFileNames(selectedTask);

          return (
            <>
              <h2 className="modal-title">{taskInfo.title}</h2>
              <p className="modal-subtitle">
                #{selectedTask.task_id} • Пользователь: {getUserName(selectedTask.user_id)}
              </p>

              <div className="section-card" style={{ marginBottom:'1.25rem' }}>
                <p style={{ margin:0, marginBottom:'1rem' }}>{taskInfo.description}</p>
                <div className="d-flex gap-3" style={{ flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:140 }}>
                    <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginBottom:4 }}>Достижение</div>
                    <strong>{getAchievementTitle(taskInfo.achievement_id)}</strong>
                  </div>
                  <div style={{ flex:1, minWidth:140 }}>
                    <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginBottom:4 }}>Ценность</div>
                    <strong style={{ color:'var(--primary-orange)' }}>+{taskInfo.percentage_value}%</strong>
                  </div>
                </div>
              </div>

              {selectedTask.submission_text && (
                <div style={{ marginBottom:'1.25rem' }}>
                  <h3 className="section-card__title" style={{ marginBottom:'0.75rem' }}>Описание выполнения</h3>
                  <div className="notification-modal-message"><p>{selectedTask.submission_text}</p></div>
                </div>
              )}

              {fileNames.length > 0 && (
                <div style={{ marginBottom:'1.25rem' }}>
                  <h3 className="section-card__title" style={{ marginBottom:'0.75rem' }}>
                    Прикреплённые файлы ({fileNames.length})
                  </h3>
                  <TaskFileList
                    filenames={fileNames}
                    folder="tasks"
                    onOpen={(i) => fileViewer.open(fileNames, i)}
                    onDownload={(fn) => fileAPI.downloadFile(fn)}
                  />
                </div>
              )}

              <div className="d-flex gap-2 justify-content-end">
                <button
                  className="btn btn-success"
                  onClick={() => { handleReviewTask(selectedTask.id, true); closeTaskModal(); }}
                >
                  Одобрить
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    const comment = prompt('Причина отклонения:');
                    if (comment) { handleReviewTask(selectedTask.id, false, comment); closeTaskModal(); }
                  }}
                >
                  Отклонить
                </button>
                <button className="btn btn-secondary" onClick={closeTaskModal}>Закрыть</button>
              </div>
            </>
          );
        })()}
      </Modal>
    </div>
  );
};

export default AdminTasks;
